import './styles/JobOffers.scss';

import { HighlightOff } from '@mui/icons-material';
import { Grid, CircularProgress, Button, Skeleton } from '@mui/material';

import { subHours, subDays, startOfHour } from 'date-fns';
import { useQueryState } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';

import { useJobTypesQuery } from 'api/hooks/jobmarket/jobtypes';
import { useJobOfferingsQuery } from 'api/hooks/jobmarket/offers';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { Modal } from 'app/components';
import { Filter } from 'app/components/Filters';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { JobOfferItem } from 'app/components/RichList/items/JobOfferItem';
import { translations } from 'locales/i18n';
import { JobOffering } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { sortCourses } from 'utils/courses';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import {
  keepContentScrollPosition,
  useContentScrollPosition
} from 'utils/hooks/useContentScrollPosition';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import { JobOfferDeactivationModal } from '../JobOfferDeactivationModal';

export function JobOffers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);
  usePageHeading(t(translations.pages.jobOffers.title()));

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const currentURL = useCurrentURL();

  useContentScrollPosition();

  return (
    <>
      <JobOffersOverview
        renderTile={jobOffering => (
          <Grid item xs={12} md={6} lg={4} className="joboffer" key={jobOffering.id}>
            <Link
              to={`/jobs/${jobOffering.id}`}
              state={{ back: currentURL }}
              onClick={keepContentScrollPosition}
            >
              <JobOfferItem key={jobOffering.id} jobOffer={jobOffering} />
            </Link>
          </Grid>
        )}
        controlElements={
          aclHelper.isJobmarketAdmin() ? (
            <Button
              startIcon={<HighlightOff />}
              variant="contained"
              color="error"
              onClick={() => {
                setDeactivateModalOpen(true);
              }}
            >
              {t(translations.pages.deactivateJobOffers.button())}
            </Button>
          ) : undefined
        }
      />
      <Modal
        open={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        headline={t(translations.pages.deactivateJobOffers.title())}
        onCancel={() => setDeactivateModalOpen(false)}
      >
        <JobOfferDeactivationModal
          jobOfferings
          closeHandler={() => setDeactivateModalOpen(false)}
        />
      </Modal>
    </>
  );
}

interface Props {
  renderTile: (tile: JobOffering) => React.ReactNode;
  controlElements?: React.ReactElement;
}

export const JobOffersOverview = (props: Props) => {
  const { t } = useTranslation();
  const offerTypeHandler = useQueryState('type', { defaultValue: '', shallow: false });
  const searchInputState = useQueryState('search', { defaultValue: '', shallow: false });
  const courseTypeHandler = useQueryState('course');
  const zipcodeInputState = useQueryState('zip');
  const publicationHandler = useQueryState('publication');
  const distanceHandler = useQueryState('distance');
  const [numberOfJoboffersShown, setNumberOfOffersShown] = useState(12);

  const { data: courses } = useCoursesQuery();

  const getDateFromPublicationEnum = (dateEnum: string | null) => {
    if (dateEnum === '') return undefined;
    switch (dateEnum) {
      case '24h':
        return subHours(startOfHour(new Date()), 24);
      case '3d':
        return subDays(startOfHour(new Date()), 3);
      case '7d':
        return subDays(startOfHour(new Date()), 7);
      case '14d':
        return subDays(startOfHour(new Date()), 14);
    }
  };

  const { data: typeData } = useJobTypesQuery();

  const {
    data: jobOfferings,
    isFetching,
    isFetched,
    isLoading
  } = useJobOfferingsQuery({
    params: {
      active: true,
      search: searchInputState[0]?.length > 0 ? searchInputState[0] : undefined,
      jobType: offerTypeHandler[0],
      course:
        courseTypeHandler[0] && courseTypeHandler[0]?.length > 0 ? courseTypeHandler[0] : undefined,
      zipcode:
        zipcodeInputState[0] && zipcodeInputState[0]?.length > 4 ? zipcodeInputState[0] : undefined,
      distance: distanceHandler[0],
      sort: 'publication',
      sortOrder: 'desc',
      limit: numberOfJoboffersShown,
      offset: 0,
      publication: getDateFromPublicationEnum(publicationHandler[0])
    }
  });

  enum Publication {
    OneDay = '24h',
    ThreeDays = '3d',
    OneWeek = '7d',
    TwoWeeks = '14d'
  }

  const publicationTime = [
    { label: t(translations.common.last24h()), name: Publication.OneDay },
    { label: t(translations.common.last3d()), name: Publication.ThreeDays },
    { label: t(translations.common.last7d()), name: Publication.OneWeek },
    { label: t(translations.common.last14d()), name: Publication.TwoWeeks }
  ];

  enum Radius {
    Ten = '10km',
    TwentyFive = '25km',
    Fifty = '50km',
    Hundred = '100km'
  }

  const radiuses = [
    { label: Radius.Ten, name: 10000 },
    { label: Radius.TwentyFive, name: 25000 },
    { label: Radius.Fifty, name: 50000 },
    { label: Radius.Hundred, name: 100000 }
  ];

  const filterJobType = (): {
    label: string;
    name: number;
  }[] => {
    return typeData
      ? typeData?.map(jt => ({
          label: jt.name,
          name: jt.id
        }))
      : [];
  };

  const filterCourses = (): {
    label: string;
    name: string;
  }[] => {
    return sortCourses(courses).map(c => ({
      label: `${c.name} (${c.shorthand})`,
      name: c.shorthand
    }));
  };

  const filters: Filter[] = [
    {
      field: 'search',
      label: t(translations.common.search()),
      stateHandler: searchInputState
    },
    {
      field: 'jobtype_id',
      label: t(translations.common.type()),
      select: {
        options: [{ label: '---', name: null }, ...filterJobType()],
        stateHandler: offerTypeHandler
      }
    },
    {
      field: 'course',
      label: t(translations.common.course()),
      select: {
        options: [{ label: '---', name: null }, ...filterCourses()],
        stateHandler: courseTypeHandler
      }
    },
    {
      field: 'publication',
      label: t(translations.pages.jobOffers.publication()),
      select: {
        options: [
          { label: '---', name: null },
          ...publicationTime.map(c => ({
            label: `${c.label}`,
            name: `${c.name}`
          }))
        ],
        stateHandler: publicationHandler
      }
    },
    {
      field: 'zipcode',
      label: t(translations.pages.jobOffers.zipcode()),
      stateHandler: zipcodeInputState,
      type: 'number',
      inputProps: {
        // Remove spinner buttons
        sx: {
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
          },
          '&[type=number]': {
            '-moz-appearance': 'textfield'
          }
        }
      }
    },
    {
      field: 'radius',
      label: t(translations.pages.jobOffers.radius()),
      select: {
        options: [
          { label: '---', name: null },
          ...radiuses.map(d => ({
            label: `${d.label}`,
            name: `${d.name}`
          }))
        ],
        stateHandler: distanceHandler
      }
    }
  ];

  return (
    <RichList
      heading={[]}
      filters={filters}
      controls={props.controlElements}
      onFilterChange={values => {}}
    >
      {jobOfferings?.data.length === 0 ? (
        <NoItems />
      ) : (
        <>
          {isLoading && (
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Grid key={index} item xs={12} md={6} lg={4}>
                  <Skeleton
                    variant="rounded"
                    height={400}
                    sx={{ display: 'flex', flex: 1, transform: 'none' }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          <Grid container>
            {jobOfferings?.data.map(jobOffering => {
              return props.renderTile(jobOffering);
            })}
          </Grid>
          {isFetched && jobOfferings?.data.length === numberOfJoboffersShown && (
            <Waypoint
              bottomOffset={'-3%'}
              onEnter={() => {
                setNumberOfOffersShown(offset => offset + numberOfJoboffersShown);
              }}
            />
          )}
          {isFetching && (
            <Grid container justifyContent="center">
              <CircularProgress />
            </Grid>
          )}
        </>
      )}
    </RichList>
  );
};
